// Navbar.jsx
import React, { useEffect, useState } from "react";
import Log from "../assets/logo.webp";
import "../styles/navigation.css";          // your CSS/SCSS build outputs here
import { useLocation, Link } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faUser } from "@fortawesome/free-regular-svg-icons/faUser";
import { faBagShopping } from "@fortawesome/free-solid-svg-icons/faBagShopping";
import { useCart } from "../CartContext";

const DESKTOP_BP = 992; // match your SCSS $mobile-breakpoint

export default function Navbar() {
  const { totalItems } = useCart();

  // --- desktop scroll-hide header state (preserved) ---
  const [showNavbar, setShowNavbar] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);

  // --- mobile overlay state ---
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  // responsive flag so we only run scroll logic on desktop
  const [isDesktop, setIsDesktop] = useState(
    typeof window !== "undefined" ? window.innerWidth >= DESKTOP_BP : true
  );

  const location = useLocation();
  const currentPath = location.pathname;

  // close mobile menu on route change
  useEffect(() => {
    setIsMenuOpen(false);
  }, [location]);

  // lock body scroll when mobile menu is open
  useEffect(() => {
    document.body.classList.toggle("nav-locked", isMenuOpen);
    return () => document.body.classList.remove("nav-locked");
  }, [isMenuOpen]);

  // keep responsive flag updated
  useEffect(() => {
    const onResize = () => setIsDesktop(window.innerWidth >= DESKTOP_BP);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // DESKTOP-ONLY: original scroll hide/show effect
  useEffect(() => {
    if (!isDesktop) return; // do nothing on mobile

    const controlNavbar = () => {
      const y = window.scrollY;
      const threshold = window.innerHeight * 0.3;
      if (y < threshold) {
        setShowNavbar(true);
      } else {
        setShowNavbar(y <= lastScrollY);
      }
      setLastScrollY(y);
    };

    window.addEventListener("scroll", controlNavbar);
    return () => window.removeEventListener("scroll", controlNavbar);
  }, [isDesktop, lastScrollY]);

  const toggleMenu = () => setIsMenuOpen((v) => !v);
  const closeMenu = () => setIsMenuOpen(false);

  return (
    <div style={{ position: "fixed", top: 0, zIndex: 100, width: "100%" }}>
      <nav className="navbar navbar-expand-lg">
        {/* Brand (desktop scroll show/hide preserved) */}
        <a
          href="#"
          className={`d-none d-md-block navbar-brand ${
            isDesktop && !showNavbar ? "hide" : "show"
          }`}
        >
          <img
            src={Log}
            alt="logo"
            className={`${isDesktop && !showNavbar ? "d-none" : "show"}`}
          />
        </a>

        {/* Hamburger / Close (fixed; animates via .is-active in CSS) */}
        <button
          className={`navbar-toggler me-4 hamburger ${
            isMenuOpen ? "is-active" : ""
          }`}
          type="button"
          aria-controls="navbarSupportedContent"
          aria-expanded={isMenuOpen}
          aria-label="Toggle navigation"
          onClick={toggleMenu}
        >
          <span className="hamburger-box">
            <span className="hamburger-inner" />
          </span>
        </button>

        {/* DESKTOP NAV (Bootstrap layout, hidden on <992px) */}
        <div
          id="navbarSupportedContent"
          className={`collapse navbar-collapse d-none d-lg-flex ${showNavbar ? "show" : "hide"}`}
        >
          <ul className="navbar-nav mx-auto mb-lg-0">
            <li className={`nav-item mx-3 ${currentPath === "/home" ? "active" : ""}`}>
              <Link className="nav-link" to="/home">Home</Link>
            </li>
            <li className={`nav-item mx-3 ${currentPath === "/packages" ? "active" : ""}`}>
              <Link className="nav-link" to="/packages">Packages</Link>
            </li>
            <li className={`nav-item mx-3 ${currentPath === "/gallery" ? "active" : ""}`}>
              <Link className="nav-link" to="/gallery">Gallery</Link>
            </li>
            <li className={`nav-item mx-3 ${currentPath === "/shop" ? "active" : ""}`}>
              <Link className="nav-link" to="/shop">Shop</Link>
            </li>
            <li className={`nav-item mx-3 ${currentPath === "/about" ? "active" : ""}`}>
              <Link className="nav-link" to="/about">About Us</Link>
            </li>
            <li className={`nav-item mx-3 ${currentPath === "/contact" ? "active" : ""}`}>
              <Link className="nav-link" to="/contact">Contact</Link>
            </li>
          </ul>

          <div className="d-flex align-items-center">
            <div className="py-3 px-2">
              <FontAwesomeIcon icon={faUser} className="card-icon" />
            </div>
            <div className="py-3 px-2 position-relative">
              <Link to="/cart" className="text-decoration-none position-relative" style={{color: "white"}}>
                <FontAwesomeIcon icon={faBagShopping} className="card-icon" />
                {totalItems > 0 && (
                  <span
                    className="position-absolute translate-middle badge rounded-pill bg-danger"
                    style={{ top: 20, right: -9, fontSize: "0.65rem", minWidth: 18 }}
                  >
                    {totalItems}
                  </span>
                )}
              </Link>
            </div>
          </div>
        </div>

        {/* MOBILE FULL-SCREEN OVERLAY NAV (solid #384837) */}
        <div className={`mobile-nav ${isMenuOpen ? "open" : ""}`}>
          <ul className="mobile-menu">
            <li onClick={closeMenu}><Link to="/home">Home</Link></li>
            <li onClick={closeMenu}><Link to="/packages">Packages</Link></li>
            <li onClick={closeMenu}><Link to="/gallery">Gallery</Link></li>
            <li onClick={closeMenu}><Link to="/shop">Shop</Link></li>
            <li onClick={closeMenu}><Link to="/about">About Us</Link></li>
            <li onClick={closeMenu}><Link to="/contact">Contact</Link></li>
          </ul>

          <div className="mobile-actions">
            <Link to="/cart" onClick={closeMenu} className="text-decoration-none">
              <FontAwesomeIcon icon={faBagShopping} className="card-icon me-2" />
              {totalItems > 0 && <span className="badge bg-danger">{totalItems}</span>}
            </Link>
          </div>
        </div>
      </nav>
    </div>
  );
}
